import { ASSISTANT_PAGE_PATH } from '@components/Assistant/assistantBridge'
import {
  AssistantPageContext,
  caseListCommand,
  pageContextFromPath,
  pageContextKey,
  pageContextListTool,
  pageContextToQuery,
  pageContextTool,
} from '@components/Assistant/assistantPageContext'
import {
  AVAILABILITY_ERROR_CODES,
  isRefusedBeforeStored,
  PRIVACY_NOTICE_REQUIRED,
  RECONCILED_ERROR_CODES,
  RETRYABLE_ERROR_CODES,
  TURN_ENGINE_ERROR_CODES,
} from '@components/Assistant/errorMessages'
import { ApiService } from '@services/ApiService'
import {
  AssistantCitation,
  AssistantClarificationEvent,
  AssistantClarificationOption,
  AssistantPart,
  streamAssistantMessage,
} from '@services/modules/AssistantStream'
import dashboardStore from '@stores/dashboard'
import { makeAutoObservable, reaction, runInAction } from 'mobx'

import {
  isExecuting,
  isOutcomeUnknown,
  toChatMessage,
  withActionStatus,
} from './assistantActions'
import {
  findStoredTurn,
  placeOfQuestion,
  QuestionPlace,
} from './assistantReconcile'
import {
  initialTurn,
  TurnEvent,
  turnReducer,
  turnToMessage,
  TurnState,
} from './assistantTurn'

/** The assistant's own error codes (ROLE_NOT_ALLOWED, FEATURE_DISABLED…), not framework ones. */
const ASSISTANT_CODE_PATTERN = /^[A-Z][A-Z_]*$/

/** The longest voice recording when the capabilities do not say (server default). */
const DEFAULT_VOICE_MAX_SECONDS = 120

/** The most records one message may tag; the server refuses a message with more. */
const MAX_MENTIONS_PER_MESSAGE = 10

/**
 * How long after a turn was cut off the server is asked what it kept of it. It needs that long
 * to store the turn's last row, and a read that follows a write too closely may not see it.
 */
export const RECONCILE_DELAY_MS = 6000

/**
 * The waits before the server is asked again when that read itself failed: right after a dropped
 * connection or a restart the server usually cannot be reached yet. After the last one the check
 * is left to Retry, which asks the server before it sends anything (see retryLastTurn).
 */
export const RECONCILE_RETRY_DELAYS_MS: readonly number[] = [10000, 20000]

/**
 * How old the capabilities may be when the drawer is opened before they are read again: the host
 * can switch the assistant off (or a skill, or the role's audience) while the page stays open.
 */
export const CAPABILITIES_MAX_AGE_MS = 60000

/**
 * The longest streamed text waits to be shown when no animation frame comes (a tab in the
 * background gets none): the answer still completes there.
 */
export const FRAME_FALLBACK_MS = 100

/**
 * Runs `run` once, at the next animation frame or after FRAME_FALLBACK_MS, whichever comes
 * first. Returns what cancels it.
 */
const onNextFrame = (run: () => void): (() => void) => {
  let frame: number | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (frame !== null) {
      cancelAnimationFrame(frame)
      frame = null
    }
  }
  const fire = () => {
    cancel()
    run()
  }
  timer = setTimeout(fire, FRAME_FALLBACK_MS)
  if (typeof requestAnimationFrame === 'function') {
    frame = requestAnimationFrame(fire)
  }
  return cancel
}

/** A question handed back to the composer: its text and the records tagged in it. */
interface DraftSnapshot {
  text: string
  mentions: AssistantDraftMention[]
}

/** What a turn sent, kept so the same question can be sent again (see retryLastTurn). */
interface SentTurn {
  conversationId: string
  content: string
  clarificationResponse?: {
    messageId: string
    selectedValue?: string
    freeText?: string
  }
  command?: string
  mentions?: { entityType: string; id: string; label: string }[]
  /** The record that was open when the question was asked: a retry asks about the same one. */
  pageContext: AssistantPageContext | null
  /** The question as it is on screen: a retry adds no second bubble for it. */
  question: AssistantChatMessage
  /** Where the question stood when the turn was sent (see reconcileTurn). */
  place: QuestionPlace
  /**
   * A typed answer to a clarification that was handed back to the composer because nothing had
   * come back for it; taken out of the composer again if the server turns out to have stored it.
   */
  handedBack?: DraftSnapshot
}

export interface AssistantConversationSummary {
  id: string
  title: string | null
  lastMessageAt: string
  isArchived: boolean
  status: string
}

/** The notice a case manager or specialist reads once before their first question. */
export interface AssistantPrivacyNotice {
  /** Sent back when the notice is closed, so a newer text is shown again. */
  version: string
  title: string
  /** One paragraph each. */
  lines: string[]
}

/** What one load of a conversation says about the messages before the ones it returned. */
interface MessagePaging {
  hasEarlier: boolean
  /** The server's cursor for the page before, sent back exactly as received. */
  earlierBefore: string | null
}

const pagingOf = (data: any): MessagePaging => {
  const earlierBefore =
    typeof data?.earlierBefore === 'string' && data.earlierBefore
      ? data.earlierBefore
      : null
  return {
    hasEarlier: data?.hasEarlier === true && earlierBefore !== null,
    earlierBefore,
  }
}

/** Compares two server timestamps (their fraction digits vary, so not as text). */
const isOlder = (a: string, b: string) => Date.parse(a) < Date.parse(b)

export interface AssistantCommand {
  name: string
  description: string
  usageHint: string
  toolName: string
}

export interface AssistantMentionType {
  type: string
  aliases: string[]
  displayName: string
  /** The typeahead of this kind returns nothing for a shorter text (absent from an older server). */
  minQueryLength?: number
  /** How to search this kind, shown while too little is typed. */
  searchHint?: string | null
}

export interface AssistantMentionResult {
  entityType: string
  id: string
  label: string
  description?: string
}

/** A tagged record living in the composer draft ("@Dr Frank Gibson"). */
export interface AssistantDraftMention {
  display: string
  entityType: string
  id: string
  label: string
}

export interface AssistantSuggestedQuery {
  category: string
  text: string
  prefillOnly?: boolean
}

export interface AssistantBriefingItem {
  label: string
  detail?: string
  overdue?: boolean
  route?: string
  query?: Record<string, string>
  /** Dashboard tab title for a record link (the case number). */
  tabTitle?: string
}

export interface AssistantBriefingSection {
  key: string
  title: string
  count: number
  items: AssistantBriefingItem[]
  askPrompt?: string
}

export interface AssistantBriefing {
  date: string
  generatedAt: string
  sections: AssistantBriefingSection[]
}

export interface AssistantDocSummary {
  key: string
  title: string
  summary: string
  commands: string[]
}

export interface AssistantDocPage extends AssistantDocSummary {
  markdown: string
}

export interface AssistantChatMessage {
  id?: string
  role: 'User' | 'Assistant'
  content: string
  citations?: AssistantCitation[]
  clarification?: AssistantClarificationEvent
  clarificationAnswered?: boolean
  /** Option value the user picked (renders the selected state on the option card). */
  clarificationSelectedValue?: string
  feedbackRating?: number | null
  partial?: boolean
  /** A question the content filter blocked: shown, but never sent to the assistant again. */
  excludedFromContext?: boolean
  /** Structured parts rendered under the answer (e.g. "Open in list view"). */
  parts?: AssistantPart[]
}

/** 1 = good answer, -1 = bad answer, 0 = the rating is taken back. */
export type AssistantFeedbackRating = 1 | -1 | 0

/** Why an answer was rated down: the codes the server accepts, with the wording shown for each. */
export const ASSISTANT_FEEDBACK_REASONS = [
  { code: 'WRONG_DATA', label: 'Wrong data' },
  { code: 'DID_NOT_ANSWER', label: 'Did not answer' },
  { code: 'TOO_SLOW', label: 'Too slow' },
  { code: 'OTHER', label: 'Other' },
] as const

export type AssistantFeedbackReason =
  (typeof ASSISTANT_FEEDBACK_REASONS)[number]['code']

/** The longest note a rating can carry; the server refuses a longer one. */
export const ASSISTANT_FEEDBACK_NOTE_MAX = 1000

export interface AssistantFeedbackDetails {
  reason?: AssistantFeedbackReason
  comment?: string
}

/**
 * Global singleton store for the AI Assistant drawer (same pattern as chatbotStore).
 * One active conversation at a time; messages stream in over SSE.
 */
export class AssistantStore {
  isOpen = false
  showHistory = false
  /** The Guide panel (user guide per skill) replaces the chat while open. */
  showGuide = false
  /** Guide page being read. */
  guideKey: string | null = null
  /** A section of that page to scroll to once it is shown (then cleared). */
  guideSection: string | null = null
  docs: AssistantDocSummary[] = []
  docPages: Record<string, AssistantDocPage> = {}
  /** The pages of the history loaded so far, newest first. */
  conversations: AssistantConversationSummary[] = []
  /** How many conversations match the history's filter on the server, loaded or not. */
  conversationsTotal = 0
  /** Older conversations than the loaded ones exist (see loadMoreConversations). */
  conversationsHasMore = false
  /** The text the history is filtered by (titles that contain it); empty = no filter. */
  conversationFilter = ''
  /**
   * The filter the conversations on screen were read with. While it is not the filter above, the
   * list still shows the rows of the previous one and the answer for the new one is on its way
   * (see conversationsFiltering).
   */
  private loadedConversationFilter = ''
  loadingMoreConversations = false
  activeConversationId: string | null = null
  messages: AssistantChatMessage[] = []
  /** The open conversation has messages older than the loaded ones (see loadEarlierMessages). */
  hasEarlierMessages = false
  loadingEarlierMessages = false
  /**
   * Set for a case manager or specialist who has not closed the privacy notice yet: it is shown
   * in place of the chat until they do. The server remembers it per user.
   */
  privacyNotice: AssistantPrivacyNotice | null = null
  /**
   * Phase of the running turn. A failed turn is back to 'idle' with an `errorCode` — there is
   * no error phase, so nothing has to reset it before the next turn or a new chat.
   */
  status: 'idle' | 'sending' | 'streaming' = 'idle'
  /** The conversation picked from the history that is being fetched; null while none is. */
  loadingConversationId: string | null = null
  /** The turn currently streaming in (see assistantTurn.ts). */
  turn: TurnState = initialTurn()
  errorCode: string | null = null
  commands: AssistantCommand[] = []
  suggestions: AssistantSuggestedQuery[] = []
  mentionTypes: AssistantMentionType[] = []
  /** Mentions tagged in the current composer draft; validated against the text on send. */
  draftMentions: AssistantDraftMention[] = []
  /**
   * What is typed in the composer. Kept here, not in the Composer, so it is still there after
   * the Guide or the History was opened over the chat, and so it always matches draftMentions.
   */
  draftText = ''
  /**
   * Counts the times the draft was put in place by something other than typing (a prefill, an
   * example from the guide, a question handed back): the Composer then moves the caret to its
   * end.
   */
  draftRevision = 0
  /**
   * Counts the times something asked for the cursor to be put in the composer (a control of the
   * panel that removed itself when it was used, and would otherwise leave the focus nowhere).
   */
  composerFocusRequest = 0
  /** Why the assistant cannot be used by this user (server code); null while it can. */
  unavailableReason: string | null = null
  /**
   * The composer offers voice input (a question spoken, transcribed by the server): the
   * assistant is available to this user, the tenant has voice input on and speech is set up.
   * Off until the capabilities say otherwise.
   */
  voiceInput = false
  /** The longest recording voice input takes; it stops by itself at this length. */
  voiceMaxSeconds = DEFAULT_VOICE_MAX_SECONDS
  /**
   * The standalone page (its own browser tab) has no screen of the app behind it: its context is
   * the record that was open when it was opened, a snapshot that never follows the app tab.
   */
  isStandalone = false
  /** That snapshot; null when the page was opened without a record, or the chip was removed. */
  standaloneContext: AssistantPageContext | null = null
  /** The record whose Context chip the user removed (see dismissPageContext). */
  private dismissedContextKey: string | null = null
  /** The record the server answered it would not use as context (see applyContextVerdict). */
  private refusedContextKey: string | null = null
  /** The record the server last accepted as context, under the record's own number or name. */
  private confirmedContext: { key: string; label: string } | null = null

  /** The cursor of the page before the loaded messages; null when there is none. */
  private earlierBefore: string | null = null
  /** What each fetched message list said about earlier messages (see adoptPaging). */
  private fetchedPaging = new WeakMap<AssistantChatMessage[], MessagePaging>()
  /** Identifies the newest request for the history; an older one that answers late is ignored. */
  private conversationListRequest = 0
  private capabilitiesLoaded = false
  /** When the capabilities were last answered by the server (Date.now()). */
  private capabilitiesLoadedAt = 0
  /** Identifies the newest capabilities request; an older one that answers late is ignored. */
  private capabilitiesRequest = 0
  private docsLoaded = false
  private abortController: AbortController | null = null
  /**
   * Identifies the newest request for the message list. A conversation load applies its
   * result only while its token is still the newest; a newer load, a new chat, a turn or
   * Stop all move it on.
   */
  private loadToken = 0
  /** The question of a turn that has not reached the server yet (see restoreUnsentDraft). */
  private unsentDraft: DraftSnapshot | null = null
  /**
   * The clarification the running turn answers, until the server shows it took the answer
   * (see reopenClarification).
   */
  private answeringClarification: {
    prompt: AssistantChatMessage
    /** The user's answer as pushed to the message list. */
    answer: AssistantChatMessage | null
    /** A typed answer goes back to the composer; a picked option has nothing to give back. */
    draft: DraftSnapshot | null
  } | null = null
  /**
   * A conversation the server created for a first message that was stopped before it was sent:
   * it is empty, so the next first message uses it instead of creating another one.
   */
  private spareConversationId: string | null = null
  /** The turn sent last, while it has not been answered (see canRetry). */
  private lastTurn: SentTurn | null = null
  /** What the server said with the error of the last failed turn (see errorMessage). */
  private serverError: { code: string; message: string } | null = null
  /** The wait before a cut-off turn is checked against the server (see reconcileTurn). */
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Titles learned from the stream (the `title` event), each with the order it arrived in: a
   * conversation list requested before a title arrived still carries the old one.
   */
  private streamedTitles = new Map<string, { title: string; seq: number }>()
  /**
   * The rating requests under way, per answer: what the server holds (`stored`), how many
   * requests have not been answered yet, and the newest one. Not observable.
   */
  private feedbackRequests = new Map<
    string,
    {
      stored: number | null | undefined
      pending: number
      last: Promise<boolean> | null
    }
  >()
  private titleSeq = 0
  /**
   * Events of the running turn that arrived since the last animation frame and are not in
   * `turn` yet (see queueTurnEvent). Nothing renders from these two: they are not observable.
   */
  private pendingEvents: TurnEvent[] = []
  /** Cancels the frame that will apply pendingEvents; null while none is waiting. */
  private cancelFrame: (() => void) | null = null

  constructor() {
    makeAutoObservable<
      AssistantStore,
      | 'pendingEvents'
      | 'cancelFrame'
      | 'screenContext'
      | 'feedbackRequests'
      | 'fetchedPaging'
    >(this, {
      pendingEvents: false,
      cancelFrame: false,
      feedbackRequests: false,
      fetchedPaging: false,
      // A plain read of the dashboard: whoever calls it tracks (or does not track) the tab.
      screenContext: false,
    })
    // Another record on screen is another context: a chip removed for the record before, or a
    // record the server would not use, says nothing about this one. The same holds for coming
    // back to that record later.
    reaction(
      () => pageContextKey(this.screenContext()),
      () => {
        this.dismissedContextKey = null
        this.refusedContextKey = null
      }
    )
  }

  get streamingContent() {
    return this.turn.content
  }

  /** Progress label of the running tool, e.g. "Searching cases…". */
  get toolActivity() {
    return this.turn.status
  }

  /** A turn is being sent or is streaming in. */
  get isTurnRunning() {
    return this.status !== 'idle'
  }

  /** A conversation picked from the history is being fetched. */
  get loadingConversation() {
    return this.loadingConversationId !== null
  }

  /** A turn is running or a conversation is loading. */
  get isBusy() {
    return this.isTurnRunning || this.loadingConversation
  }

  /**
   * Nothing new can be asked: the assistant is not available to this user, it is busy, or a
   * confirm/cancel is being applied (its follow-up is the next message of this conversation, so
   * a question must not get in before it). The next question can still be typed while it is
   * only busy (see Composer). Nothing is asked either before the privacy notice is closed.
   */
  get isInputLocked() {
    return (
      this.unavailableReason !== null ||
      this.privacyNotice !== null ||
      this.isBusy ||
      this.actionInFlight !== null
    )
  }

  /**
   * The server's own wording for the current error, which says more than the generic text of
   * its code (AI_BUSY: "Try again in about N seconds"); null when it sent none.
   */
  get errorMessage(): string | null {
    return this.errorCode !== null && this.serverError?.code === this.errorCode
      ? this.serverError.message
      : null
  }

  /**
   * The failed turn can be sent again as it was: its error is one a retry can get past, and its
   * question is still on screen in the open conversation. An answer to a clarification can be
   * sent again after any error of the turn engine: the card is answered, so there is no other
   * way to give the same answer.
   */
  get canRetry() {
    const turn = this.lastTurn
    return (
      turn !== null &&
      this.errorCode !== null &&
      (RETRYABLE_ERROR_CODES.has(this.errorCode) ||
        (turn.clarificationResponse !== undefined &&
          TURN_ENGINE_ERROR_CODES.has(this.errorCode))) &&
      !this.isInputLocked &&
      turn.conversationId === this.activeConversationId &&
      this.messages.includes(turn.question)
    )
  }

  get pendingClarification(): AssistantChatMessage | null {
    const last = this.messages[this.messages.length - 1]
    return last?.clarification && !last.clarificationAnswered ? last : null
  }

  /**
   * The record on screen, whatever the Assistant can do with it: the active dashboard tab's, or
   * on the standalone page the one it was opened with.
   */
  private screenContext(): AssistantPageContext | null {
    if (this.isStandalone) {
      return this.standaloneContext
    }
    const tab = dashboardStore.currentTab
    return pageContextFromPath(tab?.path, tab?.title)
  }

  /**
   * The record a question is asked about when it says "this case" / "this report": the one on
   * screen, unless the user removed its chip, the server answered it does not use it, or the
   * tool that reads that kind of record is not the user's. It is what the Context chip shows and
   * what is sent with the next question. Once the server has accepted the record, the label is
   * the server's (the record's own number or name).
   */
  get activePageContext(): AssistantPageContext | null {
    const context = this.screenContext()
    const key = pageContextKey(context)
    if (
      !context ||
      key === this.dismissedContextKey ||
      key === this.refusedContextKey ||
      !(context.list
        ? this.canUseListContext(context)
        : this.canUseContextOf(context.type))
    ) {
      return null
    }
    return this.confirmedContext?.key === key
      ? { ...context, label: this.confirmedContext.label }
      : context
  }

  /**
   * True when the server answered that the record on screen is not used as context (it is not
   * available to this user): said under the composer instead of a chip that names the record.
   */
  get pageContextRefused() {
    const key = pageContextKey(this.screenContext())
    return key !== null && key === this.refusedContextKey
  }

  /** With pageContextRefused: what the server does not use is a list screen, not a record. */
  get pageContextRefusedIsList() {
    return this.pageContextRefused && !!this.screenContext()?.list
  }

  /**
   * Whether the Assistant can search, for this user, the list on screen: its search tool is
   * theirs and, for a case list, that case type is on (the server lists the case type's own
   * command only then). The server decides in the end; this keeps a chip from promising a list
   * the answer would not use.
   */
  private canUseListContext = (context: AssistantPageContext) => {
    const toolName = pageContextListTool(context)
    if (!toolName || !this.canUseTool(toolName)) {
      return false
    }
    const serviceType = context.list?.serviceType
    return (
      !serviceType ||
      !this.capabilitiesLoaded ||
      this.commands.some(c => c.name === caseListCommand(serviceType))
    )
  }

  /** Whether the Assistant has, for this user, the tool that reads records of this kind. */
  private canUseContextOf = (type: string) => {
    const toolName = pageContextTool(type)
    if (toolName) {
      return this.canUseTool(toolName)
    }
    // A kind without a tool of its own is vouched for like an @-mention of that kind.
    return (
      !this.capabilitiesLoaded || this.mentionTypes.some(t => t.type === type)
    )
  }

  /** The user removed the Context chip: the record is not sent until another one is on screen. */
  dismissPageContext = () => {
    if (this.isStandalone) {
      this.standaloneContext = null
      return
    }
    this.dismissedContextKey = pageContextKey(this.screenContext())
  }

  /**
   * The standalone page tells the store it runs there, and with which record it was opened
   * (null: none). From then on the dashboard tab of this browser tab is never read.
   */
  enterStandalone = (context: AssistantPageContext | null) => {
    this.isStandalone = true
    this.standaloneContext = context
  }

  /**
   * What the server made of the context sent with a question (the `context` event): the record
   * under its own number or name, or that it is not used. The chip follows the server, so it
   * never names a record the answer was not about.
   */
  private applyContextVerdict = (
    sent: AssistantPageContext | null,
    verdict: { used?: boolean; label?: string | null }
  ) => {
    const key = pageContextKey(sent)
    if (!key) {
      return
    }
    if (verdict.used) {
      const label = typeof verdict.label === 'string' ? verdict.label : ''
      this.confirmedContext = label ? { key, label } : null
      if (this.refusedContextKey === key) {
        this.refusedContextKey = null
      }
    } else {
      this.refusedContextKey = key
      if (this.confirmedContext?.key === key) {
        this.confirmedContext = null
      }
    }
  }

  toggle = () => {
    this.isOpen ? this.close() : this.openDrawer()
  }

  openDrawer = () => {
    this.isOpen = true
    if (this.conversations.length === 0) {
      this.loadConversations()
    }
    // Asked again when the last answer was "not available" (the switch may be back on) and when
    // it is no longer fresh (the switch may have gone off): the user reads that here, not after
    // typing a question.
    this.loadCapabilities(
      this.unavailableReason !== null ||
        Date.now() - this.capabilitiesLoadedAt > CAPABILITIES_MAX_AGE_MS
    )
  }

  /**
   * Whether the assistant can use a tool for this user. The commands come from
   * the server already limited to the skills that are on for the tenant, so a
   * tool without a command there is off. True until they have loaded.
   */
  canUseTool = (toolName: string) =>
    !this.capabilitiesLoaded || this.commands.some(c => c.toolName === toolName)

  /**
   * Loads what the assistant offers this user (commands, suggestions, mention kinds) or why it
   * is not available to them. Once per page, unless `force`d: the answer can change while the
   * page is open (the host switches the feature off or on, the role's audience is closed).
   */
  loadCapabilities = async (force = false) => {
    await this.readCapabilities(force)
  }

  /** Returns whether the server answered (with the capabilities, or with a refusal). */
  private readCapabilities = async (force: boolean): Promise<boolean> => {
    if (this.capabilitiesLoaded && !force) {
      return true
    }
    const request = ++this.capabilitiesRequest
    const isNewest = () => request === this.capabilitiesRequest
    // A check made again behind the user's back says nothing when it fails: what was known
    // stays, and whoever asked for it (see checkAvailability) says so in its own place.
    const silent = this.capabilitiesLoaded
    try {
      const res = silent
        ? await ApiService.Assistant.GetCapabilities({ silent: true })
        : await ApiService.Assistant.GetCapabilities()
      runInAction(() => {
        if (!isNewest()) {
          return
        }
        this.capabilitiesLoadedAt = Date.now()
        const reason = res.data?.unavailableReason
        this.unavailableReason =
          typeof reason === 'string' && reason ? reason : null
        this.commands = res.data?.commands ?? []
        this.suggestions = res.data?.suggestedQueries ?? []
        this.mentionTypes = res.data?.mentionTypes ?? []
        this.voiceInput = res.data?.voiceInput === true
        const maxSeconds = Number(res.data?.voiceMaxSeconds)
        this.voiceMaxSeconds =
          Number.isFinite(maxSeconds) && maxSeconds > 0
            ? maxSeconds
            : DEFAULT_VOICE_MAX_SECONDS
        const notice = res.data?.privacyNotice
        this.privacyNotice =
          notice && typeof notice.version === 'string' && notice.version
            ? {
                version: notice.version,
                title: notice.title ?? '',
                lines: Array.isArray(notice.lines) ? notice.lines : [],
              }
            : null
        this.capabilitiesLoaded = true
      })
    } catch (error: any) {
      // 403: the user's role does not hold the assistant permission (or the tenant switch is
      // off). That is a state, not a failure — remember it and stop asking.
      if (error?.response?.status === 403) {
        const code = error.response.data?.error?.code
        runInAction(() => {
          if (!isNewest()) {
            return
          }
          this.unavailableReason =
            typeof code === 'string' && ASSISTANT_CODE_PATTERN.test(code)
              ? code
              : 'PERMISSION_DENIED'
          this.commands = []
          this.suggestions = []
          this.mentionTypes = []
          this.voiceInput = false
          this.privacyNotice = null
          this.capabilitiesLoaded = true
          this.capabilitiesLoadedAt = Date.now()
        })
        return true
      }
      // anything else: suggestions/commands are progressive enhancement — chat works without
      // them, and what was known before the request stays as it was
      return false
    }
    return true
  }

  /**
   * Asks the server again whether the assistant is available: after a turn was refused because
   * it is not (switched off after this page was loaded), and from the notice that says so.
   */
  reloadCapabilities = () => this.loadCapabilities(true)

  /**
   * "Check again" on the notice: the same question to the server, and whether it could be asked
   * at all — the notice says so when the answer changes nothing on screen.
   */
  checkAvailability = () => this.readCapabilities(true)

  /**
   * The user closed the privacy notice. The server keeps that with the user, so the notice is
   * not shown again on another device; only then does it go here. Returns whether it was saved.
   */
  acknowledgePrivacyNotice = async (): Promise<boolean> => {
    const notice = this.privacyNotice
    if (!notice) {
      return true
    }
    try {
      await ApiService.Assistant.AcknowledgePrivacyNotice(notice.version)
    } catch {
      return false
    }
    runInAction(() => {
      if (this.privacyNotice?.version === notice.version) {
        this.privacyNotice = null
      }
      // The question that was turned away for the notice is back in the composer; its warning
      // has been answered by closing the notice.
      if (this.errorCode === PRIVACY_NOTICE_REQUIRED) {
        this.errorCode = null
      }
    })
    return true
  }

  /** Puts the cursor in the composer (see composerFocusRequest). */
  requestComposerFocus = () => {
    this.composerFocusRequest++
  }

  applySuggestion = (suggestion: AssistantSuggestedQuery) => {
    if (suggestion.prefillOnly) {
      this.setDraft(suggestion.text)
    } else {
      this.sendMessage(suggestion.text)
    }
  }

  /** The user typed in the composer. */
  editDraft = (text: string) => {
    this.draftText = text
  }

  /**
   * Puts a text in the composer in place of what is there (a prefill-only suggestion, an
   * example from the guide), with the caret at its end.
   */
  setDraft = (text: string) => {
    this.draftText = text
    this.draftRevision++
  }

  /**
   * Hands a question back to the composer with its tagged records. The next question may have
   * been typed since it was sent (typing is not locked while a turn runs): that text is kept,
   * after the one handed back.
   */
  private giveBackDraft = (draft: DraftSnapshot) => {
    const typed = this.draftText
    if (typed.trim()) {
      this.draftText = `${draft.text}\n${typed}`
      this.draftMentions = [...draft.mentions, ...this.draftMentions]
    } else {
      this.draftText = draft.text
      this.draftMentions = draft.mentions
    }
    this.draftRevision++
  }

  /**
   * The history's filter changed and its answer has not arrived: the rows of the previous filter
   * stay on screen meanwhile, and nothing says "no match" before the server has.
   */
  get conversationsFiltering(): boolean {
    return this.conversationFilter.trim() !== this.loadedConversationFilter
  }

  /** Closes the warning shown under the messages / above the history. */
  dismissError = () => {
    this.errorCode = null
  }

  registerDraftMention = (mention: AssistantDraftMention) => {
    this.draftMentions.push(mention)
  }

  close = () => {
    this.isOpen = false
  }

  /**
   * Reads the first page of the history (again). Older pages that were already loaded with
   * "Load more" stay: the first page is what changes when a turn ends (a new conversation, a
   * new title, another order).
   */
  loadConversations = async () => {
    // A title that arrives while this request is under way is newer than its response.
    const seq = this.titleSeq
    const request = ++this.conversationListRequest
    const filter = this.conversationFilter.trim()
    try {
      const res = filter
        ? await ApiService.Assistant.GetConversations({ filter })
        : await ApiService.Assistant.GetConversations()
      runInAction(() => {
        if (request !== this.conversationListRequest) {
          return
        }
        const page = this.withStreamedTitles(res.data?.items ?? [], seq)
        const hasMore = res.data?.hasMore === true
        const oldest = page[page.length - 1]
        // What was loaded beyond the first page is older than all of it and is kept - unless
        // the rows on screen were read with another filter: those are not pages of this list.
        const sameList = filter === this.loadedConversationFilter
        this.loadedConversationFilter = filter
        const kept =
          sameList && hasMore && oldest
            ? this.conversations.filter(
                c =>
                  !page.some(item => item.id === c.id) &&
                  isOlder(c.lastMessageAt, oldest.lastMessageAt)
              )
            : []
        this.conversations = [...page, ...kept]
        this.conversationsHasMore = kept.length
          ? this.conversationsHasMore
          : hasMore
        this.conversationsTotal = Math.max(
          typeof res.data?.totalCount === 'number' ? res.data.totalCount : 0,
          this.conversations.length
        )
      })
    } catch {
      // list is non-critical; chat still works
      runInAction(() => {
        if (
          request === this.conversationListRequest &&
          filter !== this.loadedConversationFilter
        ) {
          // The rows on screen belong to another filter and this one's could not be read: they
          // must not stand for it.
          this.loadedConversationFilter = filter
          this.conversations = []
          this.conversationsHasMore = false
          this.conversationsTotal = 0
        }
      })
    }
  }

  /** The next page of the history: the conversations older than the ones loaded so far. */
  loadMoreConversations = async () => {
    const last = this.conversations[this.conversations.length - 1]
    if (
      !last ||
      !this.conversationsHasMore ||
      this.loadingMoreConversations ||
      // The rows on screen are another filter's: their next page is not this filter's.
      this.conversationsFiltering
    ) {
      return
    }
    const seq = this.titleSeq
    const request = ++this.conversationListRequest
    const filter = this.conversationFilter.trim()
    this.loadingMoreConversations = true
    try {
      const res = await ApiService.Assistant.GetConversations({
        beforeLastMessageAt: last.lastMessageAt,
        ...(filter ? { filter } : {}),
      })
      runInAction(() => {
        if (request !== this.conversationListRequest) {
          return
        }
        const known = new Set(this.conversations.map(c => c.id))
        const page = this.withStreamedTitles(res.data?.items ?? [], seq)
        this.conversations = [
          ...this.conversations,
          ...page.filter(item => !known.has(item.id)),
        ]
        this.conversationsHasMore = res.data?.hasMore === true
        this.conversationsTotal = Math.max(
          typeof res.data?.totalCount === 'number' ? res.data.totalCount : 0,
          this.conversations.length
        )
      })
    } catch {
      // "Load more" stays where it is and can be pressed again
    } finally {
      runInAction(() => {
        this.loadingMoreConversations = false
      })
    }
  }

  /**
   * Filters the history by title. The server filters (all of the user's conversations, not only
   * the loaded ones), so the list is read anew from its first page. Until that page arrives the
   * rows of the previous filter stay where they are (conversationsFiltering says so): emptying
   * the list first would flash "nothing found" at a user who is still typing.
   */
  filterConversations = async (text: string) => {
    if (text.trim() === this.conversationFilter.trim()) {
      this.conversationFilter = text
      return
    }
    this.conversationFilter = text
    await this.loadConversations()
  }

  /** A title learned from the stream after the request was sent is newer than its response. */
  private withStreamedTitles = (
    items: AssistantConversationSummary[],
    seq: number
  ): AssistantConversationSummary[] =>
    items.map(item => {
      const streamed = this.streamedTitles.get(item.id)
      return streamed && streamed.seq > seq
        ? { ...item, title: streamed.title }
        : item
    })

  /**
   * The server has titled a conversation (after the first answer): shown in the history at
   * once, without loading the list again.
   */
  private applyTitle = (conversationId: string, title: string) => {
    if (!conversationId || !title) {
      return
    }
    this.streamedTitles.set(conversationId, { title, seq: ++this.titleSeq })
    const summary = this.conversations.find(c => c.id === conversationId)
    if (summary) {
      summary.title = title
    }
  }

  newConversation = () => {
    if (this.isTurnRunning) {
      return
    }
    this.cancelConversationLoad()
    this.activeConversationId = null
    this.lastTurn = null
    this.messages = []
    this.adoptPaging(this.messages)
    this.errorCode = null
    this.draftText = ''
    this.draftMentions = []
    this.showHistory = false
    this.showGuide = false
  }

  /**
   * Moves the Assistant to its own page in a new browser tab, continuing this conversation;
   * its links keep navigating this tab.
   */
  openInNewWindow = () => {
    // The record on screen goes along as a snapshot: the new tab has no screen of its own, and
    // "this case" must still mean the case the user was looking at.
    const params = new URLSearchParams({
      ...(this.activeConversationId
        ? { conversationId: this.activeConversationId }
        : {}),
      ...pageContextToQuery(this.activePageContext),
    }).toString()
    const query = params ? `?${params}` : ''
    if (window.open(`${ASSISTANT_PAGE_PATH}${query}`, '_blank')) {
      this.close()
    }
  }

  toggleHistory = () => {
    this.showHistory = !this.showHistory
    this.showGuide = false
    // A failed pick is reported in the history; it says nothing about the chat behind it.
    if (this.errorCode === 'CONVERSATION_LOAD_FAILED') {
      this.errorCode = null
    }
  }

  /**
   * Opens the Guide panel, on `key` when given (else the page read last / the first page),
   * scrolled to `section` when given.
   */
  openGuide = (key?: string, section?: string) => {
    this.showGuide = true
    this.showHistory = false
    if (key) {
      this.guideKey = key
    }
    this.guideSection = section ?? null
    this.loadDocs()
  }

  toggleGuide = () => {
    if (this.showGuide) {
      this.showGuide = false
    } else {
      this.openGuide()
    }
  }

  loadDocs = async () => {
    if (this.docsLoaded) {
      return
    }
    try {
      const res = await ApiService.Assistant.GetDocs()
      runInAction(() => {
        this.docs = res.data ?? []
        this.docsLoaded = true
        if (!this.guideKey && this.docs.length) {
          this.guideKey = this.docs[0].key
        }
      })
    } catch {
      // the guide is optional — chat works without it
    }
  }

  loadDoc = async (key: string) => {
    if (this.docPages[key]) {
      return
    }
    try {
      const res = await ApiService.Assistant.GetDoc(key)
      runInAction(() => {
        if (res.data) {
          this.docPages[key] = res.data
        }
      })
    } catch {
      // keep the spinner-free empty page; the navigator still works
    }
  }

  /** An example prompt from the guide: back to the chat with it in the composer (not sent). */
  tryPrompt = (text: string) => {
    this.setDraft(text)
    this.showGuide = false
  }

  /**
   * Opens a conversation from the history. Picking another one while this one is still
   * loading is allowed: only the newest pick is shown, whatever order the responses arrive in.
   */
  selectConversation = async (id: string) => {
    if (this.isTurnRunning) {
      return
    }
    const token = ++this.loadToken
    this.loadingConversationId = id
    if (this.errorCode === 'CONVERSATION_LOAD_FAILED') {
      this.errorCode = null
    }
    // Superseded by a newer load, a new chat, a turn or Stop: this result must not land.
    const isStale = () => token !== this.loadToken || this.isTurnRunning
    try {
      const messages = await this.fetchMessages(id)
      runInAction(() => {
        if (isStale()) {
          return
        }
        this.loadingConversationId = null
        this.activeConversationId = id
        this.errorCode = null
        this.showHistory = false
        this.messages = messages
        this.adoptPaging(messages)
      })
    } catch {
      runInAction(() => {
        if (isStale()) {
          return
        }
        this.loadingConversationId = null
        // Shown where the user is: above the history (ConversationList) or under the messages.
        this.errorCode = 'CONVERSATION_LOAD_FAILED'
      })
    }
  }

  /** A conversation's messages as the server has them, in the shape the list renders. */
  private fetchMessages = async (
    id: string,
    /** A read the user did not ask for: its failure is not reported to them. */
    silent = false
  ): Promise<AssistantChatMessage[]> => {
    const res = silent
      ? await ApiService.Assistant.GetConversation(id, { silent: true })
      : await ApiService.Assistant.GetConversation(id)
    const messages: AssistantChatMessage[] = (res.data?.messages ?? [])
      .filter((m: any) => m.role === 'User' || m.role === 'Assistant')
      .map((m: any, index: number, all: any[]) =>
        toChatMessage(m, index === all.length - 1)
      )
    this.fetchedPaging.set(messages, pagingOf(res.data))
    return messages
  }

  /**
   * Whoever puts a fetched list on screen in place of the messages also takes over what that
   * fetch said about earlier messages: the server returns the newest ones, so earlier pages that
   * were loaded before are no longer on screen, and "Load earlier messages" starts from this list.
   */
  private adoptPaging = (messages: AssistantChatMessage[]) => {
    const paging = this.fetchedPaging.get(messages)
    this.hasEarlierMessages = paging?.hasEarlier === true
    this.earlierBefore = paging?.earlierBefore ?? null
    this.loadingEarlierMessages = false
  }

  /**
   * Loads the page of messages before the ones on screen and puts it above them. Not while a
   * turn runs or another conversation is being opened; whatever replaces the messages meanwhile
   * (another conversation, a new chat, a turn) drops the result.
   */
  loadEarlierMessages = async () => {
    const conversationId = this.activeConversationId
    const before = this.earlierBefore
    if (
      !conversationId ||
      !before ||
      !this.hasEarlierMessages ||
      this.loadingEarlierMessages ||
      this.isBusy
    ) {
      return
    }
    const token = this.loadToken
    const first = this.messages[0]
    this.loadingEarlierMessages = true
    try {
      const res = await ApiService.Assistant.GetConversation(conversationId, {
        before,
      })
      runInAction(() => {
        if (
          token !== this.loadToken ||
          this.activeConversationId !== conversationId ||
          this.earlierBefore !== before ||
          this.messages[0] !== first
        ) {
          return
        }
        const known = new Set(this.messages.map(m => m.id))
        const earlier: AssistantChatMessage[] = (res.data?.messages ?? [])
          .filter((m: any) => m.role === 'User' || m.role === 'Assistant')
          .map((m: any) => toChatMessage(m))
          .filter((m: AssistantChatMessage) => !m.id || !known.has(m.id))
        this.messages = [...earlier, ...this.messages]
        const paging = pagingOf(res.data)
        this.hasEarlierMessages = paging.hasEarlier
        this.earlierBefore = paging.earlierBefore
      })
    } catch {
      // The button stays and can be pressed again.
    } finally {
      runInAction(() => {
        this.loadingEarlierMessages = false
      })
    }
  }

  /**
   * Brings the open conversation back in line with the server after a confirm/cancel whose
   * result did not arrive: the server has the card's real state and, if the action ran, its
   * follow-up message. Unlike picking a conversation from the history it changes nothing but
   * the messages, and only while `isStale` says they are still the ones the decision was made
   * on. A reload that fails leaves the screen as it is.
   */
  private reconcileMessages = async (
    conversationId: string,
    isStale: () => boolean
  ) => {
    try {
      const messages = await this.fetchMessages(conversationId)
      runInAction(() => {
        if (!isStale()) {
          this.messages = messages
          this.adoptPaging(messages)
        }
      })
    } catch {
      // Nothing better to show than what is on screen; the warning (if any) already says so.
    }
  }

  /**
   * Drops a conversation load that is still in flight; its response is ignored. So is the
   * check of a cut-off turn against the server, whether it is waiting or already reading.
   */
  private cancelConversationLoad = () => {
    this.loadToken++
    this.loadingConversationId = null
    if (this.reconcileTimer !== null) {
      clearTimeout(this.reconcileTimer)
      this.reconcileTimer = null
    }
  }

  /**
   * A turn was cut off without the server saying how it ended (the connection dropped, the
   * stream went silent, the server restarted): the server may have kept more of it than
   * arrived here. After a moment the conversation is read again, and only when the server
   * holds this very turn is the screen brought in line with it:
   *  - it holds the finished answer: the answer is shown, the warning and Retry go — sending
   *    the question again would only ask it twice;
   *  - it holds an answer that was cut short: that one replaces what arrived here (it can be
   *    rated), and Retry stays;
   *  - it holds the answer to a clarification that was opened again here because nothing had
   *    come back: the answer is shown as given, and Retry sends it again instead of a second
   *    answer being picked.
   * In every other case the screen stays as it is: that the server shows nothing of the turn
   * yet does not mean it has nothing. A read that fails (the server is often still out of reach
   * right after the cut) is made again, a few times; and whatever this check did not settle,
   * Retry asks the server once more before it sends (see retryLastTurn). Anything the user does
   * meanwhile (another turn, Stop, another conversation, a confirm/cancel) drops the check.
   */
  private reconcileTurn = async (turn: SentTurn) => {
    const token = this.loadToken
    const isStale = () =>
      token !== this.loadToken ||
      this.isTurnRunning ||
      this.actionInFlight !== null ||
      this.lastTurn !== turn

    let stored: AssistantChatMessage[] | null = null
    for (const wait of [RECONCILE_DELAY_MS, ...RECONCILE_RETRY_DELAYS_MS]) {
      await new Promise<void>(resolve => {
        this.reconcileTimer = setTimeout(resolve, wait)
      })
      if (isStale()) {
        return
      }
      runInAction(() => {
        this.reconcileTimer = null
      })
      try {
        stored = await this.fetchMessages(turn.conversationId, true)
        break
      } catch {
        // Not reachable yet: asked again after the next wait.
        if (isStale()) {
          return
        }
      }
    }
    if (!stored) {
      // Nothing better to show than what is on screen.
      return
    }

    const messages = stored
    runInAction(() => {
      if (!isStale()) {
        this.applyStoredTurn(turn, messages)
      }
    })
  }

  /**
   * Brings the screen in line with the server's copy of the conversation when that copy holds
   * this very turn (see reconcileTurn for the cases). Returns whether the turn turned out to be
   * answered in full: it is over then, with nothing left to retry.
   */
  private applyStoredTurn = (
    turn: SentTurn,
    stored: AssistantChatMessage[]
  ): boolean => {
    const found = findStoredTurn(stored, turn.place)
    if (!found) {
      return false
    }
    if (!found.answer && this.messages.includes(turn.question)) {
      return false
    }
    this.messages = stored
    this.adoptPaging(stored)
    // The question on screen is now the server's copy of it: Retry sends that one again.
    turn.question =
      this.messages.find(m => m.id === found.question.id) ?? turn.question
    turn.place = placeOfQuestion(this.messages, turn.question)
    this.takeBackHandedDraft(turn)
    if (found.answer && !found.answer.partial) {
      this.lastTurn = null
      this.errorCode = null
      this.serverError = null
      this.loadConversations() // refresh titles / ordering
      return true
    }
    return false
  }

  /**
   * The server holds an answer that was handed back to the composer (see reopenClarification):
   * it is on screen as given now, so it leaves the composer — sent from there it would be a
   * second, unrelated question. Only while it is still there as it was handed back; what the
   * user typed after it stays.
   */
  private takeBackHandedDraft = (turn: SentTurn) => {
    const handed = turn.handedBack
    turn.handedBack = undefined
    if (!handed) {
      return
    }
    if (this.draftText === handed.text) {
      this.draftText = ''
    } else if (this.draftText.startsWith(`${handed.text}\n`)) {
      this.draftText = this.draftText.slice(handed.text.length + 1)
    } else {
      return
    }
    this.draftMentions = this.draftMentions.filter(m =>
      this.draftText.includes(m.display)
    )
    this.draftRevision++
  }

  /**
   * Gives a conversation the name the user typed. The history shows what the server stored (the
   * name on one line, as it was written), without loading the list again; a list that was asked
   * for before the rename and answers after it does not bring the old title back.
   */
  renameConversation = async (id: string, title: string) => {
    const res = await ApiService.Assistant.RenameConversation(id, title)
    const stored = typeof res?.data?.title === 'string' ? res.data.title : ''
    runInAction(() => this.applyTitle(id, stored || title.trim()))
  }

  deleteConversation = async (id: string) => {
    await ApiService.Assistant.DeleteConversation(id)
    runInAction(() => {
      if (this.conversations.some(c => c.id === id)) {
        this.conversationsTotal = Math.max(0, this.conversationsTotal - 1)
      }
      this.conversations = this.conversations.filter(c => c.id !== id)
      if (this.spareConversationId === id) {
        this.spareConversationId = null
      }
      if (this.activeConversationId === id) {
        // Its answer may still be coming in: end that turn first, or the reset is refused and
        // the next question would go to a conversation that no longer exists.
        this.stop()
        this.newConversation()
      }
    })
  }

  /**
   * Sends a question. When a clarification is pending, the text answers it.
   * `fromComposer`: the text is the composer's draft. The composer is emptied, and the text is
   * handed back there if it never reaches the server. A suggestion or briefing prompt is not:
   * whatever the user has typed in the composer stays as it is.
   */
  sendMessage = async (
    content: string,
    { fromComposer = false }: { fromComposer?: boolean } = {}
  ) => {
    const text = content.trim()
    if (!text || this.isInputLocked) {
      return
    }

    // Mentions still present in the text (the user may have edited them away).
    // Kept as tagged so they can go back to the composer if the turn never reaches the server.
    const draftMentions = this.draftMentions.filter(m =>
      text.includes(m.display)
    )
    const mentions = draftMentions
      .slice(0, MAX_MENTIONS_PER_MESSAGE)
      .map(m => ({
        entityType: m.entityType,
        id: m.id,
        label: m.label,
      }))
    const draft = fromComposer ? { text, mentions: draftMentions } : null
    if (fromComposer) {
      this.draftText = ''
      this.draftMentions = []
    }

    const pending = this.pendingClarification
    if (pending?.id && pending.clarification?.allowFreeText !== false) {
      pending.clarificationAnswered = true
      this.answeringClarification = { prompt: pending, answer: null, draft }
      await this.dispatchTurn(
        text,
        { messageId: pending.id, freeText: text },
        undefined,
        mentions
      )
      return
    }

    await this.dispatchTurn(
      text,
      undefined,
      this.detectCommand(text),
      mentions,
      draft
    )
  }

  /** "/ime-search ..." → "ime-search" when it matches a registered command. */
  private detectCommand = (text: string): string | undefined => {
    const match = /^\/([a-z0-9-]+)(\s|$)/i.exec(text)
    if (!match) {
      return undefined
    }
    return this.commands.find(
      c => c.name.toLowerCase() === match[1].toLowerCase()
    )?.name
  }

  /** User picked one of the suggested clarification options. */
  answerClarification = async (option: AssistantClarificationOption) => {
    const pending = this.pendingClarification
    if (!pending?.id || this.isInputLocked) {
      return
    }
    pending.clarificationAnswered = true
    pending.clarificationSelectedValue = option.value
    this.answeringClarification = { prompt: pending, answer: null, draft: null }
    await this.dispatchTurn(option.label, {
      messageId: pending.id,
      selectedValue: option.value,
    })
  }

  /**
   * Sends the failed turn again, as it was sent. The question is already on screen and, when
   * it reached the server, on record there: no second bubble is added and the server is told it
   * is a retry, so it does not store the question twice.
   */
  retryLastTurn = async () => {
    const turn = this.lastTurn
    if (!turn || !this.canRetry) {
      return
    }
    if (
      this.errorCode !== null &&
      RECONCILED_ERROR_CODES.has(this.errorCode) &&
      !(await this.isStillUnanswered(turn))
    ) {
      return
    }
    await this.dispatchTurn(
      turn.content,
      turn.clarificationResponse,
      turn.command,
      turn.mentions,
      null,
      turn.question,
      turn.pageContext
    )
  }

  /**
   * Before a turn that was cut off is sent again, the server is asked what it kept of it: the
   * check after the cut may have failed (still offline) or come too early, and a question the
   * server has answered would otherwise be stored and answered a second time. Returns false when
   * there is nothing to send any more: the answer was there and is on screen now, or the user
   * stopped the retry meanwhile. When the server cannot be asked the retry goes ahead — it tells
   * the server it is one, which is all that can be done.
   */
  private isStillUnanswered = async (turn: SentTurn): Promise<boolean> => {
    // Locked like a turn that is being sent: a second click or Enter finds it running, and
    // Stop ends it.
    this.status = 'sending'
    this.cancelConversationLoad()
    const token = this.loadToken
    let stored: AssistantChatMessage[] | null = null
    try {
      stored = await this.fetchMessages(turn.conversationId, true)
    } catch {
      // Out of reach: the retry itself will say so if it still is.
    }
    return runInAction(() => {
      if (
        token !== this.loadToken ||
        this.status !== 'sending' ||
        this.lastTurn !== turn
      ) {
        return false
      }
      if (stored && this.applyStoredTurn(turn, stored)) {
        this.status = 'idle'
        return false
      }
      return true
    })
  }

  /** Today's briefing (null until the user asked for it, or when unavailable). */
  briefing: AssistantBriefing | null = null
  briefingLoading = false
  /** The last load brought nothing: said next to the button that asked for it. */
  briefingFailed = false
  /** The briefing is shown in full; otherwise as one line (or, before it is loaded, a button). */
  briefingExpanded = false

  /**
   * Shows the briefing in full, loading it the first time. It is only loaded when asked for: its
   * overdue counts are expensive on the server, and not every visit to the assistant reads them.
   */
  showBriefing = async () => {
    this.briefingExpanded = true
    if (!this.briefing) {
      await this.loadBriefing()
    }
  }

  collapseBriefing = () => {
    this.briefingExpanded = false
  }

  /** Loads the briefing; the server caches it for a while unless `refresh`ed. */
  loadBriefing = async (refresh = false) => {
    if (this.briefingLoading) {
      return
    }
    this.briefingLoading = true
    this.briefingFailed = false
    try {
      const res = await ApiService.Assistant.GetBriefing(refresh)
      runInAction(() => {
        if (res.data) {
          this.briefing = res.data
        } else {
          this.briefingFailed = true
        }
      })
    } catch {
      // the briefing is a convenience — chat works without it
      runInAction(() => {
        this.briefingFailed = true
      })
    } finally {
      runInAction(() => {
        this.briefingLoading = false
      })
    }
  }

  /** Action whose confirm/cancel call is in flight (disables its buttons). */
  actionInFlight: string | null = null

  /**
   * The user decided on a confirmation card. The server runs the action (never the model),
   * marks the card and returns the assistant's follow-up message.
   *
   * When that answer does not arrive, nothing is assumed about the action: the conversation is
   * read again from the server, which knows, and the card stays locked until it has been. The
   * same when the server answers that the action is being executed by another request.
   */
  decideAction = async (actionId: string, confirm: boolean) => {
    if (this.isInputLocked) {
      return
    }
    this.actionInFlight = actionId
    // A warning about an earlier attempt says nothing about this one.
    this.errorCode = null
    const conversationId = this.activeConversationId
    // The result belongs to the messages on screen now. A new chat or another conversation
    // opened meanwhile moves the token on: the server has recorded the outcome, and it shows
    // when this conversation is opened again. Taking a new token also drops the check of a
    // cut-off turn that is under way: its copy of the messages may predate this decision.
    this.cancelConversationLoad()
    const token = this.loadToken
    const isStale = () => token !== this.loadToken
    let reconcile = false
    try {
      const res = confirm
        ? await ApiService.Assistant.ConfirmAction(actionId)
        : await ApiService.Assistant.CancelAction(actionId)
      runInAction(() => {
        if (isStale()) {
          return
        }
        const status = String(res.data?.status ?? '')
        for (const message of this.messages) {
          message.parts = withActionStatus(message.parts, actionId, status)
        }
        if (res.data?.followUp) {
          this.messages.push(toChatMessage(res.data.followUp))
        } else if (isExecuting(status)) {
          // Another tab or request is running it: its outcome is on the server, not here.
          reconcile = true
        }
      })
    } catch (error) {
      runInAction(() => {
        if (isStale()) {
          return
        }
        reconcile = true
        // A refusal the server answered with (4xx) has been shown in its own words by the
        // response interceptor; only an outcome nobody reported gets the warning here.
        if (isOutcomeUnknown(error)) {
          this.errorCode = 'ACTION_UNCONFIRMED'
        }
      })
    }
    try {
      if (reconcile && conversationId && !isStale()) {
        await this.reconcileMessages(conversationId, isStale)
      }
    } finally {
      runInAction(() => {
        this.actionInFlight = null
      })
    }
  }

  /**
   * Stops whatever is in flight, at once: the state is settled here, and the aborted request
   * finds nothing left to do when it returns. A streaming answer keeps what arrived; a
   * question that never reached the server goes back to the composer, and a clarification
   * whose answer got nothing back can be answered again.
   */
  stop = () => {
    this.abortController?.abort()
    this.abortController = null
    this.cancelConversationLoad()
    this.restoreUnsentDraft()
    this.reopenClarification()
    this.finalizePartial()
  }

  /**
   * The turn that answered a clarification ended before the server sent anything back and was
   * refused before it was stored (busy conversation, a limit, the network) or was stopped: the
   * answer is not on record, so the question is open again exactly as before, option cards
   * included. Not after an error of the turn engine itself (see onError in dispatchTurn).
   */
  private reopenClarification = () => {
    const answering = this.answeringClarification
    this.answeringClarification = null
    if (!answering) {
      return
    }
    answering.prompt.clarificationAnswered = false
    answering.prompt.clarificationSelectedValue = undefined
    const index = answering.answer
      ? this.messages.indexOf(answering.answer)
      : -1
    if (index >= 0) {
      this.messages.splice(index, 1)
    }
    if (answering.draft) {
      this.giveBackDraft(answering.draft)
      if (this.lastTurn && this.lastTurn.question === answering.answer) {
        this.lastTurn.handedBack = answering.draft
      }
    }
  }

  /** Hands a question that was never sent back to the composer, with its tagged records. */
  private restoreUnsentDraft = () => {
    if (!this.unsentDraft) {
      return
    }
    this.giveBackDraft(this.unsentDraft)
    this.unsentDraft = null
  }

  /**
   * Rates an answer (1 / -1), adds the reason and the note of a rating, or takes the rating back
   * (0). The mark changes at once; it is what the server holds again as soon as every request
   * for that answer has been answered, so a rating that was not stored never stays on screen.
   * Resolves to whether this request was stored - the caller tells the user when it was not.
   */
  submitFeedback = async (
    messageId: string,
    rating: AssistantFeedbackRating,
    details?: AssistantFeedbackDetails
  ): Promise<boolean> => {
    const message = this.messages.find(m => m.id === messageId)
    if (!message) {
      return false
    }
    let state = this.feedbackRequests.get(messageId)
    if (!state) {
      state = { stored: message.feedbackRating, pending: 0, last: null }
      this.feedbackRequests.set(messageId, state)
    }
    const requests = state
    message.feedbackRating = rating === 0 ? null : rating
    requests.pending++

    // One request per answer at a time, in the order they were made: the reason of a rating is
    // sent while the rating itself may still be on its way.
    const before = requests.last
    const send = async () => {
      if (before) {
        await before
      }
      try {
        await ApiService.Assistant.SubmitFeedback(messageId, {
          rating,
          ...details,
        })
        requests.stored = rating === 0 ? null : rating
        return true
      } catch {
        return false
      }
    }
    const request = send()
    requests.last = request
    const stored = await request

    runInAction(() => {
      requests.pending--
      if (requests.pending > 0) {
        return
      }
      this.feedbackRequests.delete(messageId)
      const shown = this.messages.find(m => m.id === messageId)
      if (shown) {
        shown.feedbackRating = requests.stored
      }
    })
    return stored
  }

  private dispatchTurn = async (
    content: string,
    clarificationResponse?: {
      messageId: string
      selectedValue?: string
      freeText?: string
    },
    command?: string,
    mentions?: { entityType: string; id: string; label: string }[],
    /** What goes back to the composer if the question never reaches the server. */
    draft: DraftSnapshot | null = null,
    /** Retry: the question of the failed turn, already on screen. */
    retryOf: AssistantChatMessage | null = null,
    /** Retry: the record that question was asked about. */
    retryContext: AssistantPageContext | null = null
  ) => {
    // What is on screen as the question is asked, not when the conversation has been created.
    const pageContext = retryOf ? retryContext : this.activePageContext
    // Lock synchronously, before the first await: callers only check isBusy once, so a
    // double Enter/click during CreateConversation would otherwise start two turns.
    this.status = 'sending'
    this.errorCode = null
    this.serverError = null
    this.turn = initialTurn()
    this.dropTurnEvents()
    this.cancelConversationLoad()
    // Created before the first await so Stop can cancel every step of the turn.
    const controller = new AbortController()
    const { signal } = controller
    this.abortController = controller

    let conversationId = this.activeConversationId
    if (!conversationId && this.spareConversationId) {
      conversationId = this.spareConversationId
      this.activeConversationId = conversationId
      this.spareConversationId = null
    }
    if (!conversationId) {
      // Nothing is on screen or on the server yet: until the conversation exists, the
      // question only lives here.
      this.unsentDraft = draft
      try {
        const res = await ApiService.Assistant.CreateConversation(signal)
        if (signal.aborted) {
          // stop() has already settled the state. The conversation exists and is empty: keep
          // it for the next first message.
          runInAction(() => {
            this.spareConversationId = res.data?.id ?? null
          })
          return
        }
        const createdId: string = res.data.id
        conversationId = createdId
        runInAction(() => {
          this.activeConversationId = createdId
          this.unsentDraft = null
        })
      } catch (error: any) {
        if (signal.aborted) {
          return
        }
        // Refused because the assistant is not available (any more): said as that, not as a
        // conversation that could not be started.
        const refusal = error?.response?.data?.error?.code
        const unavailable =
          typeof refusal === 'string' && AVAILABILITY_ERROR_CODES.has(refusal)
        runInAction(() => {
          this.restoreUnsentDraft()
          this.abortController = null
          this.errorCode = unavailable ? refusal : 'CONVERSATION_CREATE_FAILED'
          this.status = 'idle'
          if (unavailable) {
            this.reloadCapabilities()
          }
        })
        return
      }
    }

    let question: AssistantChatMessage | undefined
    const turnConversationId = conversationId
    // Whether the server has sent anything of this turn: until it has, it may have refused it.
    let received = false
    runInAction(() => {
      if (retryOf) {
        question = retryOf
      } else {
        this.messages.push({ role: 'User', content })
        question = this.messages[this.messages.length - 1]
        if (this.answeringClarification) {
          this.answeringClarification.answer = question
        }
      }
      this.lastTurn = {
        conversationId: turnConversationId,
        content,
        clarificationResponse,
        command,
        mentions,
        pageContext,
        question,
        place: placeOfQuestion(this.messages, question),
      }
    })

    // After Stop the stream may still deliver what it had buffered: that turn is over.
    const whileRunning = (apply: () => void) =>
      runInAction(() => {
        if (!signal.aborted) {
          apply()
        }
      })

    await streamAssistantMessage(
      conversationId,
      {
        content,
        clarificationResponse,
        pageContext: !pageContext
          ? undefined
          : pageContext.list
          ? {
              listKind: pageContext.type,
              serviceType: pageContext.list.serviceType,
            }
          : { recordType: pageContext.type, recordId: pageContext.id },
        // Read by a server that does not know pageContext yet.
        caseContextId:
          pageContext?.type === 'case' && !pageContext.list
            ? pageContext.id
            : undefined,
        command,
        mentions: mentions?.length ? mentions : undefined,
        retry: retryOf ? true : undefined,
      },
      {
        signal,
        onEvent: event =>
          whileRunning(() => {
            // The server is working on the turn: a clarification answer is on record.
            this.answeringClarification = null
            received = true
            if (event.type === 'token' || event.type === 'status') {
              this.status = 'streaming'
            }
            this.queueTurnEvent(event)
          }),
        onDone: ({ messageId }) =>
          whileRunning(() => this.finalizeTurn(messageId)),
        // About the record that was sent, not about the turn's state: applied also after Stop.
        onContext: verdict =>
          runInAction(() => this.applyContextVerdict(pageContext, verdict)),
        // After done, and about the conversation rather than the turn: applied whatever the
        // turn's own state is by now (the next question may already be streaming).
        onTitle: ({ conversationId: titledId, title }) =>
          runInAction(() => this.applyTitle(titledId, title)),
        onError: (code, message) =>
          whileRunning(() => {
            if (TURN_ENGINE_ERROR_CODES.has(code)) {
              // The turn engine failed after it stored the question: an answer to a
              // clarification is on record. Its bubble stays and Retry sends it again; opening
              // the card again would put a second answer on record.
              this.answeringClarification = null
            } else {
              this.reopenClarification()
              if (
                !received &&
                draft &&
                question &&
                isRefusedBeforeStored(code)
              ) {
                // Refused before anything of it was stored (too long, a limit, the session):
                // the question is not in the conversation, so it does not stay on screen as if
                // it were. It goes back to the composer, to be shortened or sent later.
                const index = this.messages.indexOf(question)
                if (index >= 0) {
                  this.messages.splice(index, 1)
                }
                this.lastTurn = null
                this.giveBackDraft(draft)
              }
            }
            this.finalizePartial()
            this.errorCode = code
            const said = typeof message === 'string' ? message.trim() : ''
            this.serverError = said ? { code, message: said } : null
            if (code === 'CONTENT_FILTERED' && question) {
              // As after a reload: the server leaves a blocked question out of later turns.
              question.excludedFromContext = true
            }
            if (RECONCILED_ERROR_CODES.has(code) && this.lastTurn) {
              this.reconcileTurn(this.lastTurn)
            }
            if (
              AVAILABILITY_ERROR_CODES.has(code) ||
              code === PRIVACY_NOTICE_REQUIRED
            ) {
              // Switched off after this page was loaded, or a privacy notice this page has not
              // shown yet: the capabilities say which, so they are read again now instead of
              // letting the next question find out the same way.
              this.reloadCapabilities()
            }
          }),
      }
    )
  }

  /**
   * Takes an event of the running turn. Text arrives a few characters at a time, many times per
   * frame, and every change of `turn` renders the growing answer again (markdown and all): text
   * is collected and applied once per animation frame. Anything else (a status, a part,
   * citations, a clarification) is applied at once, after the text that came before it.
   */
  private queueTurnEvent = (event: TurnEvent) => {
    this.pendingEvents.push(event)
    if (event.type !== 'token') {
      this.flushTurnEvents()
    } else if (!this.cancelFrame) {
      this.cancelFrame = onNextFrame(this.flushTurnEvents)
    }
  }

  /** Applies the collected events to `turn`, in the order they arrived. */
  private flushTurnEvents = () => {
    const events = this.pendingEvents
    this.dropTurnEvents()
    if (events.length) {
      this.turn = events.reduce(turnReducer, this.turn)
    }
  }

  private dropTurnEvents = () => {
    this.cancelFrame?.()
    this.cancelFrame = null
    this.pendingEvents = []
  }

  private finalizeTurn = (messageId: string) => {
    this.flushTurnEvents()
    this.answeringClarification = null
    this.lastTurn = null
    const message = turnToMessage(this.turn, messageId)
    if (message) {
      this.messages.push(message)
    }
    this.turn = initialTurn()
    this.status = 'idle'
    this.loadConversations() // refresh titles / ordering
  }

  /** Stream stopped early: keep what arrived as a partial answer. */
  private finalizePartial = () => {
    // Text still waiting for its frame is part of what arrived.
    this.flushTurnEvents()
    const message = turnToMessage(this.turn)
    if (message) {
      this.messages.push(message)
    }
    this.turn = initialTurn()
    this.status = 'idle'
  }
}

const assistantStore = new AssistantStore()

export default assistantStore
